import { THEMES, type ThemeId } from '@cuboom/shared';

/**
 * Per-theme background layers, drawn in screen space between the sky
 * gradient and the world: sun, stars, drifting clouds, city skylines,
 * abstract shapes, aurora, embers, a perspective grid — and the two
 * parallax hill bands every theme has always had.
 *
 * Static geometry (stars, buildings, cloud blobs, shapes) is generated once
 * per theme and cached, so a frame only pays for offsets and cheap anims —
 * phones are the target.
 */

type Layer = 'sun' | 'stars' | 'clouds' | 'skyline' | 'shapes' | 'aurora' | 'embers' | 'grid' | 'hills';

interface BackdropSpec {
  layers: Layer[];
  /** Screen-fraction sun (required when 'sun' is listed): x/y/r + colors. */
  sun?: { x: number; y: number; r: number; color: string; glow: string };
  skyline?: 'day' | 'night';
  shapes?: 'soft' | 'bars';
}

/** Sun positions/colors per sun-bearing theme — fractions of the viewport. */
const SPECS: Record<ThemeId, BackdropSpec> = {
  meadow: { layers: ['sun', 'clouds', 'hills'], sun: { x: 0.78, y: 0.2, r: 34, color: '#fff6d8', glow: 'rgba(255,240,180,' } },
  forest: { layers: ['clouds', 'hills'] },
  desert: { layers: ['sun', 'hills'], sun: { x: 0.82, y: 0.16, r: 42, color: '#fff2c8', glow: 'rgba(255,226,150,' } },
  snow: { layers: ['sun', 'clouds', 'hills'], sun: { x: 0.2, y: 0.18, r: 30, color: '#ffffff', glow: 'rgba(220,236,255,' } },
  city: { layers: ['clouds', 'skyline', 'hills'], skyline: 'day' },
  skyline: { layers: ['clouds', 'skyline', 'hills'], skyline: 'day' },
  neon: { layers: ['stars', 'skyline', 'hills'], skyline: 'night' },
  canyon: { layers: ['sun', 'hills'], sun: { x: 0.16, y: 0.18, r: 38, color: '#fff0c0', glow: 'rgba(255,214,150,' } },
  highlands: { layers: ['clouds', 'hills'] },
  islands: { layers: ['sun', 'clouds', 'hills'], sun: { x: 0.5, y: 0.14, r: 36, color: '#fff7dd', glow: 'rgba(255,241,190,' } },
  harbor: { layers: ['clouds', 'hills'] },
  lagoon: { layers: ['sun', 'clouds', 'hills'], sun: { x: 0.75, y: 0.18, r: 34, color: '#fffbe8', glow: 'rgba(210,255,240,' } },
  candy: { layers: ['clouds', 'shapes', 'hills'], shapes: 'soft' },
  sunset: { layers: ['sun', 'clouds', 'hills'], sun: { x: 0.5, y: 0.44, r: 64, color: '#ffd9a0', glow: 'rgba(255,154,86,' } },
  void: { layers: ['stars', 'aurora', 'hills'] },
  prism: { layers: ['shapes', 'hills'], shapes: 'bars' },
  magma: { layers: ['embers', 'hills'] },
  mint: { layers: ['clouds', 'hills'] },
  golden: { layers: ['sun', 'clouds', 'hills'], sun: { x: 0.3, y: 0.22, r: 44, color: '#ffe9a8', glow: 'rgba(255,206,120,' } },
  orchid: { layers: ['stars', 'shapes', 'hills'], shapes: 'soft' },
  mono: { layers: ['grid', 'hills'] },
};

// --- per-theme static caches ------------------------------------------------

interface Cache {
  stars?: { fx: number; fy: number; r: number; tw: number }[];
  clouds?: { fx: number; fy: number; rx: number; ry: number; speed: number; depth: number }[];
  buildings?: { x: number; w: number; h: number; lit: number }[]; // strip 0..STRIP
  shapes?: { fx: number; fy: number; r: number; hue: number; speed: number; phase: number }[];
}
const cache = new Map<ThemeId, Cache>();

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic 0..1 from a seed + stream. */
function rnd(seed: number, stream: number): number {
  let t = (seed + Math.imul(stream, 0x9e3779b9)) | 0;
  t = Math.imul(t ^ (t >>> 16), 0x45d9f3b);
  t = Math.imul(t ^ (t >>> 16), 0x45d9f3b);
  return ((t ^ (t >>> 16)) >>> 0) / 4294967296;
}

function getCache(id: ThemeId): Cache {
  let c = cache.get(id);
  if (c) return c;
  c = {};
  const seed = hashStr(id);

  if (SPECS[id].layers.includes('stars')) {
    c.stars = Array.from({ length: 90 }, (_, i) => ({
      fx: rnd(seed, i * 3 + 1),
      fy: rnd(seed, i * 3 + 2) * 0.55,
      r: 0.6 + rnd(seed, i * 3 + 3) * 1.4,
      tw: 1 + rnd(seed, i * 3 + 4) * 2.5,
    }));
  }
  if (SPECS[id].layers.includes('clouds')) {
    c.clouds = Array.from({ length: 7 }, (_, i) => ({
      fx: rnd(seed, i * 5 + 10),
      fy: 0.08 + rnd(seed, i * 5 + 11) * 0.3,
      rx: 60 + rnd(seed, i * 5 + 12) * 90,
      ry: 16 + rnd(seed, i * 5 + 13) * 18,
      speed: 3 + rnd(seed, i * 5 + 14) * 7, // px/s drift
      depth: 0.4 + rnd(seed, i * 5 + 15) * 0.6, // parallax weight
    }));
  }
  if (SPECS[id].layers.includes('skyline')) {
    const strip = 2048;
    const buildings: NonNullable<Cache['buildings']> = [];
    let x = 0;
    let k = 0;
    while (x < strip) {
      const bw = 60 + rnd(seed, k * 4 + 20) * 120;
      const bh = 0.18 + rnd(seed, k * 4 + 21) * 0.34; // fraction of horizon band
      buildings.push({ x, w: bw, h: bh, lit: rnd(seed, k * 4 + 22) });
      x += bw + 8 + rnd(seed, k * 4 + 23) * 40;
      k++;
    }
    c.buildings = buildings;
  }
  if (SPECS[id].layers.includes('shapes')) {
    c.shapes = Array.from({ length: 6 }, (_, i) => ({
      fx: 0.1 + rnd(seed, i * 4 + 30) * 0.8,
      fy: 0.12 + rnd(seed, i * 4 + 31) * 0.34,
      r: 50 + rnd(seed, i * 4 + 32) * 110,
      hue: rnd(seed, i * 4 + 33),
      speed: 2 + rnd(seed, i * 4 + 34) * 5,
      phase: rnd(seed, i * 4 + 35) * 6.28,
    }));
  }
  cache.set(id, c);
  return c;
}

// --- layer painters ---------------------------------------------------------

function drawSun(ctx: CanvasRenderingContext2D, w: number, h: number, spec: BackdropSpec): void {
  const s = spec.sun;
  if (!s) return;
  const cx = s.x * w;
  const cy = s.y * h;
  const glow = ctx.createRadialGradient(cx, cy, s.r * 0.4, cx, cy, s.r * 3.2);
  glow.addColorStop(0, `${s.glow}0.5)`);
  glow.addColorStop(1, `${s.glow}0)`);
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, s.r * 3.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = s.color;
  ctx.beginPath();
  ctx.arc(cx, cy, s.r, 0, Math.PI * 2);
  ctx.fill();
}

function drawStars(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  nowS: number,
  c: Cache,
): void {
  for (const st of c.stars ?? []) {
    const a = 0.35 + 0.55 * (0.5 + 0.5 * Math.sin(nowS * st.tw + st.fx * 40));
    ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(st.fx * w, st.fy * h, st.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawClouds(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  nowS: number,
  camX: number,
  themeId: ThemeId,
  c: Cache,
): void {
  // Smoky over the ember sky, soft white everywhere else.
  ctx.fillStyle = themeId === 'magma' ? 'rgba(120,70,55,.35)' : 'rgba(255,255,255,.5)';
  for (const cl of c.clouds ?? []) {
    const span = w + cl.rx * 4;
    const drift = (cl.fx * span + nowS * cl.speed) % span;
    const px = drift - cl.rx * 2 - camX * 0.04 * cl.depth;
    const x = ((px % span) + span) % span - cl.rx * 2;
    const y = cl.fy * h;
    ctx.beginPath();
    ctx.ellipse(x, y, cl.rx, cl.ry, 0, 0, Math.PI * 2);
    ctx.ellipse(x + cl.rx * 0.55, y - cl.ry * 0.4, cl.rx * 0.6, cl.ry * 0.75, 0, 0, Math.PI * 2);
    ctx.ellipse(x - cl.rx * 0.5, y - cl.ry * 0.25, cl.rx * 0.55, cl.ry * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawSkyline(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  camX: number,
  spec: BackdropSpec,
  c: Cache,
): void {
  const night = spec.skyline === 'night';
  const horizon = h * 0.62;
  const par = -camX * 0.06;
  const strip = 2048;
  const off = ((par % strip) + strip) % strip;
  ctx.fillStyle = night ? 'rgba(16,8,34,.92)' : 'rgba(110,124,146,.55)';
  for (const bld of c.buildings ?? []) {
    for (const base of [off - strip, off]) {
      const bx = bld.x + base;
      if (bx > w + 40 || bx + bld.w < -40) continue;
      const bh = bld.h * (horizon - h * 0.18);
      ctx.fillRect(bx, horizon - bh, bld.w, bh);
      // Windows: lit speckle by night, glass strips by day.
      const cols = Math.max(2, Math.floor(bld.w / 18));
      const rows = Math.max(2, Math.floor(bh / 26));
      for (let r = 0; r < rows; r++) {
        for (let col = 0; col < cols; col++) {
          const on = rnd((bld.lit * 1e6) | 0, r * 31 + col) > 0.45;
          if (night) {
            if (!on) continue;
            const hue = 185 + ((r * 47 + col * 13) % 2) * 130; // cyan ↔ magenta
            ctx.fillStyle = `hsla(${hue}, 90%, 65%, .8)`;
            ctx.fillRect(bx + 6 + col * 18, horizon - bh + 8 + r * 26, 8, 11);
            ctx.fillStyle = night ? 'rgba(16,8,34,.92)' : 'rgba(110,124,146,.55)';
          } else {
            ctx.fillStyle = 'rgba(190,210,235,.4)';
            ctx.fillRect(bx + 6 + col * 18, horizon - bh + 8 + r * 26, 8, 11);
            ctx.fillStyle = 'rgba(110,124,146,.55)';
          }
        }
      }
    }
  }
}

function drawShapes(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  nowS: number,
  camX: number,
  themeId: ThemeId,
  spec: BackdropSpec,
  c: Cache,
): void {
  // Bar bands: sunset-coral, cyan, violet (prism). Soft circles derive their
  // tint from the theme hash below instead.
  const hues = [14, 190, 268];
  for (const [i, sh] of (c.shapes ?? []).entries()) {
    const drift = Math.sin(nowS * 0.12 * sh.speed + sh.phase) * 24;
    const px = sh.fx * w + drift - camX * 0.05 + (i % 2) * 40;
    const py = sh.fy * h + Math.cos(nowS * 0.1 * sh.speed + sh.phase) * 14;
    if (spec.shapes === 'bars') {
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(-0.5);
      ctx.fillStyle = `hsla(${hues[i % hues.length]}, 85%, 62%, .16)`;
      ctx.fillRect(-sh.r, -sh.r * 0.22, sh.r * 2, sh.r * 0.44);
      ctx.restore();
    } else {
      // Soft circles: palette-derived tints (hill/grass hues as HSL seed).
      const h1 = (hashStr(themeId) + i * 67) % 360;
      ctx.fillStyle = `hsla(${h1}, 70%, 70%, .14)`;
      ctx.beginPath();
      ctx.arc(px, py, sh.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawAurora(ctx: CanvasRenderingContext2D, w: number, h: number, nowS: number): void {
  for (let rib = 0; rib < 3; rib++) {
    const hue = 140 + rib * 55; // green → teal → blue-violet
    const yBase = h * (0.14 + rib * 0.08);
    ctx.strokeStyle = `hsla(${hue}, 85%, 62%, ${0.16 - rib * 0.03})`;
    ctx.lineWidth = 26 - rib * 6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let sx = -20; sx <= w + 20; sx += 26) {
      const y =
        yBase +
        Math.sin(sx * 0.006 + nowS * 0.5 + rib * 2.1) * 34 +
        Math.sin(sx * 0.013 - nowS * 0.3 + rib) * 16;
      sx === -20 ? ctx.moveTo(sx, y) : ctx.lineTo(sx, y);
    }
    ctx.stroke();
  }
}

function drawEmbers(ctx: CanvasRenderingContext2D, w: number, h: number, nowS: number): void {
  for (let i = 0; i < 26; i++) {
    const speed = 26 + rnd(7, i) * 40;
    const life = (nowS * speed * 0.01 + rnd(7, i + 100)) % 1;
    const x = rnd(7, i + 200) * w + Math.sin(nowS * 0.8 + i) * 30;
    const y = h - life * h * 0.75;
    const a = (1 - life) * 0.7;
    ctx.fillStyle = `rgba(255,${120 + ((i * 37) % 80)},40,${a.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, 1.4 + (i % 3) * 0.8, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number, camX: number): void {
  // Perspective grid fading up toward the horizon — mono's only ornament.
  const horizon = h * 0.5;
  ctx.strokeStyle = 'rgba(0,0,0,.16)';
  ctx.lineWidth = 1;
  // Horizontal rungs, spacing tightening toward the horizon.
  for (let i = 1; i <= 9; i++) {
    const t = i / 9;
    const y = horizon + (h - horizon) * t * t;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  // Verticals converging on a vanishing point (drifts with the camera).
  const vpx = w / 2 - (camX * 0.03) % 120;
  for (let i = -8; i <= 8; i++) {
    ctx.beginPath();
    ctx.moveTo(vpx + i * 26, horizon);
    ctx.lineTo(vpx + i * w * 0.14, h);
    ctx.stroke();
  }
}

function drawHills(ctx: CanvasRenderingContext2D, w: number, h: number, themeId: ThemeId, camX: number): void {
  const theme = THEMES[themeId];
  const band = (
    color: string,
    baseY: number,
    amp: number,
    offsetX: number,
    freq: number,
    phase: number,
  ): void => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let sx = 0; sx <= w; sx += 8) {
      const wx = sx + offsetX;
      const y =
        baseY +
        Math.sin(wx * freq + phase) * amp +
        Math.sin(wx * freq * 2.7 + phase * 2) * amp * 0.4;
      ctx.lineTo(sx, y);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
  };
  band(theme.hillBack, h * 0.55, 90, -camX * 0.12, 0.0016, 3.1);
  band(theme.hillFront, h * 0.66, 70, -camX * 0.28, 0.0031, 1.7);
}

// --- entry ------------------------------------------------------------------

/** Draw every background layer for the theme, between sky fill and world. */
export function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  themeId: ThemeId,
  camX: number,
  w: number,
  h: number,
  nowS: number,
): void {
  const spec = SPECS[themeId];
  const c = getCache(themeId);
  for (const layer of spec.layers) {
    switch (layer) {
      case 'sun':
        drawSun(ctx, w, h, spec);
        break;
      case 'stars':
        drawStars(ctx, w, h, nowS, c);
        break;
      case 'clouds':
        drawClouds(ctx, w, h, nowS, camX, themeId, c);
        break;
      case 'skyline':
        drawSkyline(ctx, w, h, camX, spec, c);
        break;
      case 'shapes':
        drawShapes(ctx, w, h, nowS, camX, themeId, spec, c);
        break;
      case 'aurora':
        drawAurora(ctx, w, h, nowS);
        break;
      case 'embers':
        drawEmbers(ctx, w, h, nowS);
        break;
      case 'grid':
        drawGrid(ctx, w, h, camX);
        break;
      case 'hills':
        drawHills(ctx, w, h, themeId, camX);
        break;
    }
  }
}
